/**
 * /visits/:visitId/edit — pre-populated form for editing a visit's notes,
 * rating, and visited_at. Wires `useUpdateVisit`.
 *
 * Architecture: a thin param-validation wrapper (VisitEditRoute) reads the
 * router param and validates it. The inner form component (VisitEditForm)
 * hosts ALL hooks so the rules-of-hooks invariant holds across param
 * transitions on the same component instance.
 */
import * as React from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import type { VisitId, VisitItem } from '@shared/types';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Skeleton } from '../../components/ui/Skeleton';
import { TextInput } from '../../components/ui/TextInput';
import { TextArea } from '../../components/ui/TextArea';
import { FormField } from '../../components/ui/FormField';
import { DotRating, type DotValue } from '../../components/ui/DotRating';
import { EmptyState } from '../../components/ui/EmptyState';
import { AddPhotoButton } from '../../components/AddPhotoButton';
import { PhotoGallery } from '../../components/PhotoGallery';
import { useActiveHouseholdId } from '../../lib/household';
import {
  useCreateVisitItem,
  useDeleteVisitItem,
  useUpdateVisitItem,
  useUpdateVisit,
  useVisit,
} from '../../lib/queries';
import { useToast } from '../../lib/store';
import { useErrorToasts } from '../../lib/errorToast';

export default function VisitEditRoute() {
  const { visitId } = useParams<{ visitId: string }>();
  if (!visitId || !visitId.startsWith('vst_')) {
    return <Navigate to="/" replace />;
  }
  return <VisitEditForm visitId={visitId as VisitId} />;
}

function VisitEditForm({ visitId: id }: { visitId: VisitId }) {
  const navigate = useNavigate();
  const householdId = useActiveHouseholdId();
  const toast = useToast();
  const errors = useErrorToasts();

  const visit = useVisit(householdId, id);
  const update = useUpdateVisit(householdId, id);

  const [visitedAt, setVisitedAt] = React.useState('');
  const [rating, setRating] = React.useState<DotValue>(0);
  const [notes, setNotes] = React.useState('');
  const [hydrated, setHydrated] = React.useState(false);
  const [touched, setTouched] = React.useState(false);

  React.useEffect(() => {
    if (!visit.data || hydrated) return;
    setVisitedAt(visit.data.visited_at.slice(0, 10));
    setRating(visit.data.rating as DotValue);
    setNotes(visit.data.notes ?? '');
    setHydrated(true);
  }, [visit.data, hydrated]);

  if (visit.isPending) {
    return (
      <div data-testid="visit-edit-skeleton">
        <Skeleton className="h-3 w-24 mb-3" />
        <Skeleton className="h-12 w-2/3 mb-12" />
        <div className="space-y-6 max-w-lg">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  if (visit.error || !visit.data) {
    return (
      <EmptyState
        title="We couldn't load that entry."
        description="It may have been removed, or you may have lost connection."
        cta={
          <Button variant="primary" onClick={() => navigate('/')}>
            Back home
          </Button>
        }
      />
    );
  }

  const placeId = visit.data.place_id;
  const photoIds = visit.data.photo_ids ?? [];
  const dateError = touched && !visitedAt ? 'When did you visit?' : '';
  const ratingError =
    touched && rating === 0 ? 'Pick a rating from 1 to 5 dots.' : '';
  const formInvalid = Boolean(dateError) || Boolean(ratingError);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setTouched(true);
    if (!visitedAt || rating === 0) return;
    try {
      await update.mutateAsync({
        visited_at: `${visitedAt}T12:00:00Z`,
        rating: rating as 1 | 2 | 3 | 4 | 5,
        notes: notes.trim() || undefined,
      });
      toast.success('Entry updated.');
      navigate(`/places/${placeId}`);
    } catch (err) {
      if (!errors.handle(err)) {
        toast.error(
          err instanceof Error ? err.message : "We couldn't save the entry.",
        );
      }
    }
  }

  return (
    <article>
      <PageHeader eyebrow="Edit entry" title="Polish the details." />

      <form onSubmit={onSubmit} noValidate className="space-y-6 max-w-lg">
        {formInvalid && touched ? (
          <div
            className="px-4 py-3 border border-error-muted/40 bg-error-soft"
            role="alert"
          >
            <p className="font-serif italic text-sm text-error-muted leading-snug">
              A few details need attention.
            </p>
          </div>
        ) : null}

        <FormField
          label="Date"
          htmlFor="edit-visit-date"
          {...(dateError ? { error: dateError } : {})}
        >
          <TextInput
            id="edit-visit-date"
            type="date"
            value={visitedAt}
            onChange={(e) => setVisitedAt(e.target.value)}
            required
          />
        </FormField>

        <FormField
          label="Rating"
          {...(ratingError ? { error: ratingError } : {})}
        >
          <DotRating value={rating} onChange={(v) => setRating(v)} size="lg" />
        </FormField>

        <FormField label="Notes" optional htmlFor="edit-visit-notes">
          <TextArea
            id="edit-visit-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="What stood out about this visit?"
            minRows={4}
          />
        </FormField>

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate(`/places/${placeId}`)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={update.isPending}
            loadingLabel="Saving…"
            disabled={update.isPending}
          >
            Save changes
          </Button>
        </div>
      </form>

      <VisitItemsEditor
        visitId={id}
        householdId={householdId}
        items={visit.data.items ?? []}
      />

      <section className="mt-12 max-w-lg" data-testid="visit-photos-section">
        <header className="mb-3 flex items-baseline justify-between">
          <h2 className="font-serif text-xl text-ink-900">Photos</h2>
          <AddPhotoButton visitId={id} label="Add photo" />
        </header>
        <p className="font-serif italic text-sm text-ink-500 leading-snug mb-4">
          Photos attached to this entry. Add as many as you like.
        </p>
        <PhotoGallery photoIds={photoIds} limit={6} />
      </section>
    </article>
  );
}

/**
 * Inline editor for a visit's items (dishes / rooms / work performed). Each
 * row saves on blur (name/notes) or on dot-rating change. "Add item" creates
 * a blank row immediately so it gets a server id to PATCH against; the ×
 * removes it. The visit query re-fetches on each mutation to stay in sync.
 */
function VisitItemsEditor({
  visitId,
  householdId,
  items,
}: {
  visitId: VisitId;
  householdId: ReturnType<typeof useActiveHouseholdId>;
  items: VisitItem[];
}) {
  const toast = useToast();
  const errors = useErrorToasts();
  const create = useCreateVisitItem(householdId, visitId);
  const update = useUpdateVisitItem(householdId, visitId);
  const remove = useDeleteVisitItem(householdId, visitId);

  function handleError(err: unknown, fallback: string) {
    if (!errors.handle(err)) {
      toast.error(err instanceof Error ? err.message : fallback);
    }
  }

  async function commitField(
    item: VisitItem,
    patch: Partial<Pick<VisitItem, 'name' | 'rating' | 'notes'>>,
  ) {
    // Skip no-op saves so a blur without changes doesn't hit the network.
    const changed = Object.entries(patch).some(
      ([k, v]) => item[k as keyof VisitItem] !== v,
    );
    if (!changed) return;
    try {
      await update.mutateAsync({ itemId: item.id, patch });
    } catch (err) {
      handleError(err, "We couldn't update that item.");
    }
  }

  async function addItem() {
    try {
      await create.mutateAsync({ name: 'New item', rating: null, notes: null });
    } catch (err) {
      handleError(err, "We couldn't add an item.");
    }
  }

  async function removeItem(itemId: VisitItem['id']) {
    try {
      await remove.mutateAsync({ itemId });
    } catch (err) {
      handleError(err, "We couldn't remove that item.");
    }
  }

  return (
    <section className="mt-12 max-w-lg" data-testid="visit-items-section">
      <header className="mb-3 flex items-baseline justify-between">
        <h2 className="font-serif text-xl text-ink-900">Items</h2>
        <button
          type="button"
          onClick={addItem}
          disabled={create.isPending}
          className="text-xs uppercase tracking-widest text-ink-500 hover:text-ink-900 focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent disabled:opacity-50"
        >
          Add item
        </button>
      </header>

      {items.length === 0 ? (
        <p className="font-serif italic text-sm text-ink-500 leading-snug">
          No dishes, rooms, or work logged for this entry yet.
        </p>
      ) : (
        <ul className="space-y-6">
          {items.map((item) => (
            <li
              key={item.id}
              className="border-b border-ink-100 pb-6 last:border-b-0"
            >
              <div className="flex items-start justify-between gap-3">
                <input
                  defaultValue={item.name}
                  aria-label="Item name"
                  onBlur={(e) =>
                    commitField(item, { name: e.target.value.trim() })
                  }
                  className="flex-1 bg-transparent border-0 border-b border-ink-200 px-0 py-2 font-serif text-base text-ink-900 focus:border-b-2 focus:border-accent focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => removeItem(item.id)}
                  aria-label={`Remove ${item.name}`}
                  className="mt-1 text-xs uppercase tracking-widest text-ink-500 hover:text-error-muted focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent"
                >
                  Remove
                </button>
              </div>
              <div className="mt-3 flex items-center gap-3">
                <DotRating
                  value={(item.rating ?? 0) as DotValue}
                  onChange={(v) => commitField(item, { rating: v })}
                  size="sm"
                />
              </div>
              <input
                defaultValue={item.notes ?? ''}
                aria-label="Item notes"
                placeholder="Notes on this item (optional)"
                onBlur={(e) =>
                  commitField(item, { notes: e.target.value.trim() || null })
                }
                className="mt-3 w-full bg-transparent border-0 border-b border-ink-200 px-0 py-2 font-serif italic text-sm text-ink-700 focus:border-b-2 focus:border-accent focus:outline-none"
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
